import ReactGA, { type GaOptions } from "react-ga";

export type AnalyticsEvent = {
  action: string;
  category: string;
  label?: string;
  value?: number;
  nonInteraction?: boolean;
};

export type PageViewOptions = {
  pageTitle?: string;
  pageLocation?: string;
  customParameters?: Record<string, unknown>;
};

export type UserProperties = {
  userId?: string;
  customParameters?: Record<string, unknown>;
};

export type AnalyticsConfig = {
  debug?: boolean;
  testMode?: boolean;
  options?: GaOptions;
};
/**
 * Analytics adapter class that wraps react-ga4 with a clean, type-safe interface
 * Provides methods for tracking page views, events, and user properties
 */
export class AnalyticsAdapter {
  private isInitialized = false;
  private config: AnalyticsConfig;
  private debugMode: boolean;

  constructor(config: AnalyticsConfig) {
    this.config = config;
    this.debugMode = config.debug ?? process.env.NODE_ENV === "development";
  }

  /**
   * Initialize Google Analytics with the provided configuration
   */
  public initialize(): void {
    if (this.isInitialized) {
      this.log("Analytics already initialized");
      return;
    }

    if (!process.env.REACT_APP_GOOGLE_ANALYTICS_KEY) {
      console.warn("Analytics not initialized. Missing configuration values.");
      return;
    }

    try {
      ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_KEY, {
        testMode: this.config.testMode ?? false,
        gaOptions: this.config.options ?? {},
      });

      this.isInitialized = true;
      this.log("Analytics initialized successfully");
    } catch (error) {
      console.error("Failed to initialize analytics:", error);
    }
  }

  /**
   * Check if analytics is properly initialized
   */
  public isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Track a page view
   */
  public trackPageView(path: string, options?: PageViewOptions): void {
    if (!this.ensureInitialized()) return;

    try {
      ReactGA.send({
        hitType: "pageview",
        page: path,
        title: options?.pageTitle,
        location: options?.pageLocation,
        ...options?.customParameters,
      });

      this.log("Page view tracked:", { path, options });
    } catch (error) {
      console.error("Failed to track page view:", error);
    }
  }

  /**
   * Track a custom event
   */
  public trackEvent(event: AnalyticsEvent): void {
    if (!this.ensureInitialized()) return;

    try {
      ReactGA.event({
        category: event.category,
        action: event.action,
        label: event.label,
        value: event.value,
        nonInteraction: event.nonInteraction ?? false,
      });

      this.log("Event tracked:", event);
    } catch (error) {
      console.error("Failed to track event:", error);
    }
  }

  /**
   * Set user properties
   */
  public setUserProperties(properties: UserProperties): void {
    if (!this.ensureInitialized()) return;

    try {
      if (properties.userId) {
        ReactGA.set({ userId: properties.userId });
      }

      if (properties.customParameters) {
        ReactGA.set(properties.customParameters);
      }

      this.log("User properties set:", properties);
    } catch (error) {
      console.error("Failed to set user properties:", error);
    }
  }

  /**
   * Ensure analytics is initialized before tracking
   */
  private ensureInitialized(): boolean {
    if (!this.isInitialized) {
      console.warn("Analytics not initialized. Call initialize() first.");
      return false;
    }
    return true;
  }

  /**
   * Internal logging method
   */
  private log(message: string, data?: unknown): void {
    if (this.debugMode) {
      console.log(`[Analytics] ${message}`, data ?? "");
    }
  }
}
